import { useQuery } from '@tanstack/react-query';

import OffersService from '~/services/OffersService';

import { offerQueryKeys } from './useGetOffer';
import type { OpportunityRoom } from './useGetOffer';

export type OpportunityRelatedExtraGuestSurcharge = {
  age_range__c?: string | null;
  bedding_type__c?: string | null;
  collected_by__c?: string | null;
  cost_price__c?: number | string | null;
  cost_price_currency?: string | null;
  currency?: string | null;
  converted_cost__c?: number | string | null;
  extra_night_margin_check__c?: string | null;
  extra_guest_type__c?: string | null;
  id: string;
  inclusions__c?: string | null;
  margin__c?: number | string | null;
  name?: string | null;
  opportunity__c?: string | null;
  other_extra_bedding_type__c?: string | null;
  sell_price_aud__c?: number | string | null;
  synced_at?: string | null;
};

export type OpportunityRelatedDeal = OpportunityRoom & {
  bundled_experiences__c?: string | null;
  channel_fare_type__c?: string | null;
  child_age_permitted__c?: string | null;
  number_of_bundled_experience__c?: number | string | null;
  offer_discount__c?: number | string | null;
  travel_end_date__c?: string | null;
  travel_start_date__c?: string | null;
};

export type OpportunityRelatedBundledExperience = {
  ancillary_deal_option__c?: string | null;
  bundled_experience_opportunity_book_by__c?: string | null;
  bundled_experience_opportunity_travel_to__c?: string | null;
  channel_fare_type__c?: string | null;
  converted_cost_aud__c?: number | string | null;
  currencyisocode?: string | null;
  deal_option__c?: string | null;
  fare_type_display__c?: string | null;
  id: string;
  inactive__c?: boolean | null;
  is_bundled_experience_ending_before_opp__c?: boolean | null;
  name?: string | null;
  opportunityid__c?: string | null;
  rrp__c?: number | string | null;
  sell_price_uplift__c?: number | string | null;
  sell_price_uplift_number__c?: number | string | null;
  synced_at?: string | null;
  tag__c?: string | null;
  view_in_led__c?: string | null;
};

export type OpportunityRelatedInclusion = {
  id: string;
  deal_offer__c?: string | null;
  description__c?: string | null;
  inclusion_description__c?: string | null;
  name?: string | null;
  rrp__c?: number | string | null;
  synced_at?: string | null;
  total_value__c?: number | string | null;
  type__c?: string | null;
};

export type OpportunityRelatedRecords = {
  bundled_experiences?: Array<OpportunityRelatedBundledExperience>;
  deals?: Array<OpportunityRelatedDeal>;
  extra_guest_surcharges?: Array<OpportunityRelatedExtraGuestSurcharge>;
  inclusions?: Array<OpportunityRelatedInclusion>;
  rooms?: Array<OpportunityRoom>;
};

export function useGetOpportunityRelated(
  opportunityId: string | undefined,
  { enabled = true }: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: offerQueryKeys.opportunityRelated(opportunityId),
    queryFn: async () => {
      if (!opportunityId) {
        throw new Error('opportunityId is required');
      }

      const response = await OffersService.getOpportunityRelated(opportunityId);
      return response.result as OpportunityRelatedRecords;
    },
    enabled: enabled && !!opportunityId,
  });
}
